import { MenuItem, Stack } from '@mui/material';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Sector } from '@/domain/impact/impact.entity';
import {
  CargoValues,
  Category,
  ImpactCo2Answer,
  ValuesCargo,
} from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { language, translate } from '@/ui/i18n';
import {
  AddNewQuiz,
  NewQuizEdition,
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/components/quiz-panel/co2/components/QuizComponents';
import TagForm from '@/ui/pages/app/components/quiz-panel/co2/components/TagForm.tsx';
import { useImpactCo2AnswerSubmitDisabled } from '@/ui/pages/app/components/quiz-panel/co2/hooks/useImpactCo2AnswerSubmitDisabled';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { isNumber } from '@/ui/utils/form-validations';

const CATEGORY: Category = 'CARGO';

export function CargoForm({
  sector,
  onSubmit,
  onDelete,
  onEdit,
  isExternal = false,
  initialValues,
  hideDistance = false,
}: {
  sector: Sector;
  onSubmit(answer: ImpactCo2Answer): void;
  onDelete(answer: ImpactCo2Answer): void;
  onEdit(answer: ImpactCo2Answer): void;
  isExternal?: boolean;
  initialValues?: {
    totalDistance?: number;
  };
  hideDistance?: boolean;
}) {
  const { hideCompleted, panelOpen, answersSelectorOverride } = useContext(
    ImpactQuestionContext,
  );
  const [displayNew, setDisplayNew] = useState(false);
  const [displayTag, setDisplayTag] = useState(false);
  const [editing, setEditing] = useState<Array<number>>([]);

  const defaultAnswersSelector = selectImpactCo2Answers<ValuesCargo>({
    sector: sector,
    category: CATEGORY,
    isExternal: isExternal,
  });
  let answersSelector = defaultAnswersSelector;
  if (answersSelectorOverride) {
    answersSelector = answersSelectorOverride;
  }
  const answers: ImpactCo2Answer<ValuesCargo>[] =
    useAppSelector(answersSelector);

  const { disableSubmitActions } = useImpactCo2AnswerSubmitDisabled();

  const handleSubmit = (values: ValuesCargo) => {
    const answer: ImpactCo2Answer = {
      id: -1,
      userId: '',
      sector: sector,
      category: CATEGORY,
      values: values,
      isExternal: isExternal,
      source: '',
    };
    onSubmit(answer);
    setDisplayNew(false);
  };

  const handleEdit = (values: ValuesCargo, id: number) => {
    const answer: ImpactCo2Answer = {
      id,
      userId: '',
      sector,
      category: CATEGORY,
      values,
      isExternal: isExternal,
      source: '',
    };
    onEdit(answer);
    removeFromEditing(answer.id);
  };

  const handleDelete = () => {
    setDisplayNew(false);
  };

  const addToEditing = (id: number) => {
    setEditing([...editing, id]);
  };

  const removeFromEditing = (id: number) => {
    setEditing([...editing].filter((i) => i !== id));
  };

  return (
    <>
      {/* check if we have to hide question if already completed */}
      {!(hideCompleted && answers.length > 0) && (
        <>
          {/* already created answers */}
          {answers
            .sort((a, b) => a.id - b.id)
            .map((answer, i) => (
              <div key={`cargo` + i}>
                {editing.includes(answer.id) && !!panelOpen ? (
                  <QuizEdition
                    answer={answer}
                    removeFromEditing={removeFromEditing}
                    index={i}
                    setDisplayTag={() => setDisplayTag(!displayTag)}
                    title={translate(
                      `ImpactQuestion.CARGO.transportationModeTitleBySector.${sector}`,
                    )}
                    form={
                      <Form
                        sector={sector}
                        onSubmit={(values) => handleEdit(values, answer.id)}
                        defaultValues={answer.values}
                        onDelete={() => onDelete(answer)}
                        answersCount={answers.length}
                        disableSubmit={disableSubmitActions}
                        disableDelete={disableSubmitActions}
                        isExternal={isExternal}
                        displayTag={displayTag}
                        hideDistance={hideDistance}
                      />
                    }
                  />
                ) : (
                  <QuizNotEdition
                    addToEditing={addToEditing}
                    answeredValues={[
                      translate(
                        `ImpactQuestion.CARGO.transportationModeValues.${answer.values.transportationMode}`,
                      ),
                      `${answer.values.weight.toLocaleString(language)} ${translate('ImpactQuestion.CARGO.weight-unit', { count: answer.values.weight })}`,
                      `${answer.values.travelDistance.toLocaleString(
                        language,
                      )} km`,
                    ]}
                    title={translate(
                      `ImpactQuestion.CARGO.transportationModeTitleBySector.${sector}`,
                    )}
                    index={i}
                    answer={answer}
                    quizLabel={answer.values.label}
                    score={answer.values.score}
                  />
                )}
              </div>
            ))}

          {/* new answer */}
          {displayNew || answers.length === 0 ? (
            <NewQuizEdition
              answersLength={answers.length}
              title={translate(
                `ImpactQuestion.CARGO.transportationModeTitleBySector.${sector}`,
              )}
              setDisplayTag={() => setDisplayTag(!displayTag)}
              form={
                <Form
                  sector={sector}
                  onSubmit={handleSubmit}
                  defaultValues={{
                    ...emptyFormInitialValues,
                    travelDistance:
                      initialValues?.totalDistance ||
                      emptyFormInitialValues.travelDistance,
                  }}
                  onDelete={() => handleDelete()}
                  answersCount={answers.length}
                  disableSubmit={disableSubmitActions}
                  disableDelete={disableSubmitActions}
                  isExternal={isExternal}
                  displayTag={displayTag}
                  hideDistance={hideDistance}
                />
              }
            />
          ) : (
            <AddNewQuiz
              setDisplayNew={setDisplayNew}
              buttonTitle={translate('ImpactQuestion.CARGO.add-button-title')}
            />
          )}
        </>
      )}
    </>
  );
}

const emptyFormInitialValues: ValuesCargo = {
  transportationMode: 'VAN',
  weight: 0,
  travelDistance: 0,
  label: '',
  score: 0,
  tags: [],
};

function Form({
  sector,
  onSubmit,
  onDelete,
  defaultValues,
  answersCount,
  disableSubmit,
  disableDelete,
  isExternal,
  displayTag,
  hideDistance = false,
}: {
  sector: Sector;
  onSubmit: (values: ValuesCargo) => void;
  onDelete: () => void;
  defaultValues?: ValuesCargo;
  answersCount: number;
  disableSubmit: boolean;
  disableDelete: boolean;
  displayTag: boolean;
  isExternal?: boolean;
  hideDistance?: boolean;
}) {
  const initialValues: ValuesCargo = defaultValues || emptyFormInitialValues;

  const { handleSubmit, register, formState, setValue } = useForm<ValuesCargo>({
    defaultValues: initialValues,
  });

  const { errors } = formState;

  const handleFormSubmit = (data: ValuesCargo) => {
    const values = {
      ...data,
      travelDistance: parseFloat(data.travelDistance.toString()),
      weight: parseFloat(data.weight.toString()),
    };
    onSubmit(values);
  };

  return (
    <Stack>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack>
          {!isExternal && (displayTag || initialValues.tags.length > 0) && (
            <TagForm
              handleChange={(tags) => setValue('tags', tags)}
              defaultValue={initialValues.tags}
            />
          )}
          <FSTextField
            type="text"
            defaultValue={initialValues.label}
            register={{
              ...register('label'),
            }}
            error={!!errors.label}
            helperText={errors.label?.message}
            placeholder={translate('ImpactQuestion.add-title')}
            placeholderColor="#A29BAE"
            variant="standard"
            sx={{ mb: 2 }}
          />
          <FSTextField
            select
            defaultValue={initialValues.transportationMode}
            label={translate(
              `ImpactQuestion.CARGO.transportationModeTitleBySector.${sector}`,
            )}
            register={{
              ...register('transportationMode'),
            }}
            error={!!errors.transportationMode}
            helperText={errors.transportationMode?.message}
          >
            {CargoValues.map((value) => (
              <MenuItem key={value} value={value}>
                {translate(
                  `ImpactQuestion.CARGO.transportationModeValues.${value}`,
                )}
              </MenuItem>
            ))}
          </FSTextField>

          <FSTextField
            type="number"
            label={translate('ImpactQuestion.CARGO.weightTitle')}
            defaultValue={initialValues.weight}
            register={{
              ...register('weight', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.weight}
            helperText={errors.weight?.message}
            step={0.01}
          />

          {!hideDistance && (
            <FSTextField
              type="number"
              label={translate('ImpactQuestion.CARGO.travelDistanceTitle')}
              defaultValue={initialValues.travelDistance}
              register={{
                ...register('travelDistance', {
                  required: translate('Form.fill-the-field'),
                  validate: isNumber,
                }),
              }}
              error={!!errors.travelDistance}
              helperText={errors.travelDistance?.message}
              step={0.01}
            />
          )}

          <Stack direction={'row'} marginTop={1} gap={1}>
            <FSPrimaryButton type="submit" disabled={disableSubmit}>
              {translate(defaultValues ? 'Form.save' : 'Form.add')}
            </FSPrimaryButton>
            {onDelete && answersCount > 0 && (
              <FSSecondaryButton onClick={onDelete} disabled={disableDelete}>
                {translate('Form.delete')}
              </FSSecondaryButton>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
